import React, { useContext, useEffect } from 'react';
import SeoHead from '@/components/common/seo-head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { Api } from '@/api';
import MainLayout from '@/layouts/main';
import AppTitle from '@/components/common/title';
import { routes } from '@/infrastructure/const/routes';
import AppContainer from '@/components/layout/container';
import { localeList } from '@/public/locales/locale-list';
import { IBreadcrumb } from '@/components/common/breadcrumbs';
import { GetStaticProps, NextLayoutComponentType } from 'next';
import { SiteContentType } from '@/infrastructure/enum/site-content.enum';
import { ISiteContent } from '@/infrastructure/interfaces/i-site-content';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getLocaleForUrl } from '@/infrastructure/functions/get-locale-for-url';
import { getSiteContentMarkup } from '@/infrastructure/functions/get-site-content-markup';
import {
  BreadcrumbContext,
  IBreadcrumbContextType,
} from '@/infrastructure/context/breadcrumb-context';
import {
  baseMarkupSchema,
  breadcrumbListMarkupSchema,
  organizationMarkupSchema,
  webPageMarkupSchema,
  webSiteMarkupSchema,
} from '@/infrastructure/markup-schemas';
import s from './styles.module.scss';
import { decryptPageProps } from '@/utils/crypto/page-props-decrypt';
import { IEncryptedProps } from '@/infrastructure/interfaces/i-encrypted-props';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL;

interface ISupportPage {
  content: ISiteContent;
}

const SupportPage: NextLayoutComponentType<IEncryptedProps> = (props) => {
  const { data } = props;
  const decrypted = decryptPageProps<ISupportPage>(data);
  const { content } = decrypted;

  const { setBreadcrumbs } = useContext(
    BreadcrumbContext
  ) as IBreadcrumbContextType;

  const router = useRouter();
  const { locale } = router;

  const { t: tMeta } = useTranslation('meta');
  const { t: tLinks } = useTranslation('links');
  const { t: tCommon } = useTranslation('common');

  const canonicalURL = `${SITE_URL}${getLocaleForUrl(locale)}` + routes.support;

  const breadcrumbs: IBreadcrumb[] = [
    {
      title: tLinks('home'),
      path: routes.home,
    },
    {
      title: tLinks('support'),
      path: routes.aboutUs,
    },
  ];

  const schema = baseMarkupSchema([
    webPageMarkupSchema(
      routes.support,
      tMeta('supportPagetitle'),
      tMeta('homePageDescription')
    ),
    webSiteMarkupSchema(),
    organizationMarkupSchema(),
    breadcrumbListMarkupSchema(breadcrumbs),
  ]);

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SeoHead
        title={tMeta('supportPageTitle')}
        description={tMeta('aboutUsPageDescription')}
        canonicalURL={canonicalURL}
        schema={schema}
      />
      <main>
        <AppContainer>
          <AppTitle title={tCommon('supportTitle')} main />
          <div className={s['content']}>
            <div dangerouslySetInnerHTML={getSiteContentMarkup(content)} />
          </div>
        </AppContainer>
      </main>
    </>
  );
};

SupportPage.getLayout = (page) => <MainLayout>{page}</MainLayout>;

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { locale = '' } = ctx;
  const api = Api.getInstance();

  const { data: content } = await api.getSiteContentByType(
    SiteContentType.SUPPORT
  );

  const dataToEncrypt = {
    content,
  };
  const { encryptPageProps } =
    await import('@/utils/crypto/page-props-encrypt');
  const encrypted = encryptPageProps(dataToEncrypt);

  return {
    props: {
      data: encrypted,
      ...(await serverSideTranslations(locale, localeList)),
    },
  };
};

export default SupportPage;
